import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Pill, CheckCircle2, ShieldAlert } from 'lucide-react';
import { authApi } from '@/api/auth';

type State =
  | { kind: 'loading' }
  | { kind: 'ok'; already: boolean }
  | { kind: 'dead' }
  | { kind: 'missing' };

export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [state, setState] = useState<State>(token ? { kind: 'loading' } : { kind: 'missing' });

  // La confirmation CONSOMME le lien : elle ne doit partir qu'une fois, alors
  // que React monte deux fois en développement (StrictMode). Sans ce garde, le
  // second appel trouverait le jeton déjà consommé et afficherait « lien mort »
  // juste après l'avoir validé.
  const fired = useRef(false);

  useEffect(() => {
    if (!token || fired.current) return;
    fired.current = true;
    authApi
      .verifyEmail(token)
      .then((r) => setState({ kind: 'ok', already: r.alreadyVerified }))
      .catch(() => setState({ kind: 'dead' }));
  }, [token]);

  const shell = (children: React.ReactNode) => (
    <div className="w-full min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-[400px]">
        <div className="flex items-center gap-2.5 mb-8 justify-center">
          <div className="w-9 h-9 rounded-xl bg-accent flex items-center justify-center">
            <Pill size={19} color="#fff" strokeWidth={2.25} />
          </div>
          <div>
            <div className="text-[17px] font-semibold text-slate-900">Officina</div>
            <div className="text-[10px] uppercase tracking-[0.12em] text-slate-400">RH Pharmacie</div>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-8">{children}</div>
      </div>
    </div>
  );

  if (state.kind === 'loading') {
    return shell(<div className="text-[13px] text-slate-500 py-4">Confirmation en cours…</div>);
  }

  if (state.kind === 'ok') {
    return shell(
      <>
        <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-4">
          <CheckCircle2 size={21} strokeWidth={2} />
        </div>
        <h2 className="text-[20px] font-semibold tracking-tight text-slate-900">
          {state.already ? 'Adresse déjà confirmée' : 'Adresse confirmée'}
        </h2>
        <p className="text-[13px] text-slate-500 mt-2 leading-relaxed">
          {state.already
            ? 'Cette adresse était déjà confirmée. Vous pouvez vous connecter.'
            : 'Votre adresse est confirmée. Vous pouvez maintenant accéder à votre espace.'}
        </p>
        <Link
          to="/login"
          className="mt-6 w-full inline-flex items-center justify-center bg-primary hover:bg-primary-hover text-white font-medium text-[13px] py-2.5 rounded-lg transition-colors"
        >
          Se connecter
        </Link>
      </>
    );
  }

  return shell(
    <>
      <div className="w-11 h-11 rounded-xl bg-danger-bg text-danger flex items-center justify-center mb-4">
        <ShieldAlert size={21} strokeWidth={2} />
      </div>
      <h2 className="text-[20px] font-semibold tracking-tight text-slate-900">Lien inutilisable</h2>
      <p className="text-[13px] text-slate-500 mt-2 leading-relaxed">
        {state.kind === 'missing'
          ? 'Ce lien est incomplet. Ouvrez-le depuis le mail reçu, sans le modifier.'
          : 'Ce lien de confirmation a expiré ou a déjà servi. Connectez-vous : nous vous proposerons d’en recevoir un nouveau.'}
      </p>
      <Link
        to="/login"
        className="mt-6 w-full inline-flex items-center justify-center bg-primary hover:bg-primary-hover text-white font-medium text-[13px] py-2.5 rounded-lg transition-colors"
      >
        Revenir à la connexion
      </Link>
    </>
  );
}
